import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';
import { TicketCategory, TicketScope } from 'stores/TicketTypeStoreAdapter';

// --- Enum定義 ---

/** タスクステータス */
export const TaskStatus = {
  NOT_STARTED: '未着手',
  IN_PROGRESS: '着手中',
  DONE: '完了',
  CLOSED: 'クローズ',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type TaskStatus = keyof typeof TaskStatus;

/** 承認ステータス */
export const ApprovalStatus = {
  ADJUSTING: '調整中',
  SUBMITTED: '申請中',
  IN_PROGRESS: '対応中',
  APPROVED: '承認',
  REJECTED: '否決',
  CANCELLED: '取消',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ApprovalStatus = keyof typeof ApprovalStatus;

// --- 型定義 ---

/** フォーム値入力 */
export type TicketFormValueInput = {
  itemKey: string;
  values: string[];
};

/** チケットサマリ（親/子/兄弟） */
export type TicketSummaryResult = {
  ticketId: string;
  managementId: string;
  title: string;
  status: string;
};

/** ウォッチャー */
export type TicketWatcherResult = {
  userId: string;
  userName: string;
};

/** 詳細のフォーム項目（値付き） */
export type TicketDetailFormItem = {
  itemKey: string;
  label: string;
  formType: string;
  values: string[];
};

/** 詳細のフォームグループ */
export type TicketDetailFormGroup = {
  groupId: string;
  name: string;
  items: TicketDetailFormItem[];
};

/** チケット取得APIレスポンス（API7104） */
export type TicketDetailResult = {
  ticketId: string;
  managementId: string;
  typeId: string;
  typeName: string;
  ticketCategory: TicketCategory;
  scope: TicketScope;
  keyPrefix: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  creatorUserId: string;
  creatorUserName: string;
  assigneeUserId: string;
  assigneeUserName: string;
  approverUserId: string | null;
  approverUserName: string | null;
  ownerUserId: string | null;
  ownerUserName: string | null;
  parentTicketId: string | null;
  scheduledStartDate: string | null;
  scheduledEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  createdAt: string;
  updatedAt: string;
  watchedByMe: boolean;
  formGroups: TicketDetailFormGroup[];
  unassignedItems: TicketDetailFormItem[];
  parentTicket: TicketSummaryResult | null;
  childTickets: TicketSummaryResult[];
  siblingTickets: TicketSummaryResult[];
  watchers: TicketWatcherResult[];
};

/** チケット一覧の1件（API7105） */
export type TicketListResult = {
  ticketId: string;
  managementId: string;
  typeId: string;
  typeName: string;
  ticketCategory: TicketCategory;
  scope: TicketScope;
  keyPrefix: string | null;
  title: string;
  status: string;
  priority: string | null;
  assigneeUserId: string;
  assigneeUserName: string;
  approverUserId: string | null;
  approverUserName: string | null;
  parentTicketId: string | null;
  scheduledStartDate: string | null;
  scheduledEndDate: string | null;
  actualStartDate: string | null;
  updatedAt: string;
};

/** チケット一覧取得APIレスポンス（API7105） */
export type ListTicketResponse = {
  totalCount: number;
  results: TicketListResult[];
};

// --- リクエスト型 ---

/** タスクチケット登録（API7101） */
export type CreateTaskTicketParams = {
  typeId: string;
  title: string;
  description?: string;
  priority?: string;
  assigneeUserId?: string;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  parentTicketId?: string;
  formValues?: TicketFormValueInput[];
};

/** タスクチケット更新（API7102） */
export type UpdateTaskTicketParams = {
  ticketId: string;
  title: string;
  description?: string;
  priority?: string;
  assigneeUserId: string;
  formValues?: TicketFormValueInput[];
  notify?: boolean;
};

/** 承認チケット登録（API7106） */
export type CreateApprovalTicketParams = {
  typeId: string;
  title: string;
  description?: string;
  approverUserId: string;
  assigneeUserId?: string;
  parentTicketId?: string;
  formValues?: TicketFormValueInput[];
  submit?: boolean;
};

/** 承認チケット更新（API7107） */
export type UpdateApprovalTicketParams = {
  ticketId: string;
  title: string;
  description?: string;
  approverUserId: string;
  formValues?: TicketFormValueInput[];
  notify?: boolean;
};

/** チケット取得（API7104） */
export type GetTicketParams = {
  ticketId: string;
};

/** チケット一覧取得（API7105） */
export type ListTicketParams = {
  typeIds?: string[];
  ticketCategory?: TicketCategory;
  scope?: TicketScope;
  statuses?: string[];
  priorities?: string[];
  assigneeUserId?: string;
  assigneeUserIds?: string[];
  creatorUserId?: string;
  approverUserId?: string;
  parentTicketId?: string;
  keyword?: string;
  managementId?: string;
  watchedByMe?: boolean;
  onlyParent?: boolean;
  overdueOnly?: boolean;
  scheduledStartFrom?: string;
  scheduledStartTo?: string;
  scheduledEndFrom?: string;
  scheduledEndTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  countOnly?: boolean;
};

/** タスクステータス更新（API7111） */
export type UpdateTaskStatusParams = {
  ticketId: string;
  status: TaskStatus;
};

/** タスク担当者変更（API7112） */
export type UpdateTaskAssigneeParams = {
  ticketId: string;
  assigneeUserId: string;
};

/** タスク日付一括更新（API7113） */
export type TaskScheduleInput = {
  ticketId: string;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
};

export type UpdateTaskScheduleParams = {
  schedules: TaskScheduleInput[];
};

/** 承認ステータス更新（API7121） */
export type UpdateApprovalStatusParams = {
  ticketId: string;
  targetStatus: 'SUBMITTED' | 'IN_PROGRESS' | 'REJECTED' | 'CANCELLED';
};

/** 承認（API7122） */
export type ApproveTicketParams = {
  ticketId: string;
};

// --- 共通 ---
const authHeader = () => ({
  headers: { 'X-Session-Id': localStorage.getItem('session-id') },
});

const handleError = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response) {
    const { status, data } = error.response;
    throw createApiError(status, data?.message || error.message);
  }
  throw new ServerError();
};

// --- API関数 ---

/**
 * タスクチケット登録API (API7101)
 * @param params.typeId - 種別ID（必須, 有効なTASK種別）
 * @param params.title - タイトル（必須, 最大300文字）
 * @param params.assigneeUserId - 担当者ID（任意, 未指定時は作成者）
 * @param params.scheduledEndDate - 終了予定日（任意, 未指定時は9999-12-31）
 */
export const createTaskTicket = async (params: CreateTaskTicketParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.CREATE_TASK_TICKET, params, authHeader());
    return response.data.ticketId;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * タスクチケット更新API (API7102)
 * @param params.ticketId - チケットID（必須, 最大64文字）
 * @param params.title - タイトル（必須, 最大300文字）
 * @param params.assigneeUserId - 担当者ID（必須, PRIVATEは所有者以外不可）
 * @param params.notify - 通知する（任意, デフォルトfalse）
 */
export const updateTaskTicket = async (params: UpdateTaskTicketParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TASK_TICKET, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * 承認チケット登録API (API7106)
 * @param params.typeId - 種別ID（必須, 有効なAPPROVAL種別）
 * @param params.title - タイトル（必須, 最大300文字）
 * @param params.approverUserId - 承認者ID（必須）
 * @param params.submit - 申請フラグ（任意, true=即申請/false=一時保存）
 */
export const createApprovalTicket = async (params: CreateApprovalTicketParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.CREATE_APPROVAL_TICKET, params, authHeader());
    return response.data.ticketId;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * 承認チケット更新API (API7107)
 * @param params.ticketId - チケットID（必須, 最大64文字。編集は作成者のみ）
 * @param params.title - タイトル（必須, 最大300文字）
 * @param params.approverUserId - 承認者ID（必須）
 */
export const updateApprovalTicket = async (params: UpdateApprovalTicketParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_APPROVAL_TICKET, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * チケット取得API (API7104)
 * @param params.ticketId - チケットID（必須, 最大64文字）
 */
export const getTicket = async (params: GetTicketParams): Promise<TicketDetailResult> => {
  try {
    const response = await axios.post(KanzhongBackend.GET_TICKET, params, authHeader());
    return response.data.result;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * チケット一覧取得API (API7105)
 * @param params - 各種検索条件（keyword=部分一致 / その他=完全一致・IN・範囲）
 * @param params.countOnly - trueで results を返さず totalCount のみ
 */
export const listTicket = async (params: ListTicketParams): Promise<ListTicketResponse> => {
  try {
    const response = await axios.post(KanzhongBackend.LIST_TICKET, params, authHeader());
    return { totalCount: response.data.totalCount, results: response.data.results ?? [] };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * チケット件数取得（API7105 の countOnly=true ラッパー）
 */
export const countTicket = async (params: ListTicketParams): Promise<number> => {
  const res = await listTicket({ ...params, countOnly: true });
  return res.totalCount;
};

/**
 * タスクステータス更新API (API7111)
 * @param params.ticketId - チケットID（必須, 最大64文字）
 * @param params.status - タスクステータス（必須, NOT_STARTED/IN_PROGRESS/DONE/CLOSED）
 */
export const updateTaskStatus = async (params: UpdateTaskStatusParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TASK_STATUS, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * タスク担当者変更API (API7112)
 * @param params.ticketId - チケットID（必須, 最大64文字）
 * @param params.assigneeUserId - 担当者ID（必須, PRIVATEは所有者以外不可）
 */
export const updateTaskAssignee = async (params: UpdateTaskAssigneeParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TASK_ASSIGNEE, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * タスク日付一括更新API (API7113)
 * @param params.schedules - 更新対象チケットの配列（予定/実績の開始・終了日）
 */
export const updateTaskSchedule = async (params: UpdateTaskScheduleParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TASK_SCHEDULE, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * 承認ステータス更新API (API7121)
 * @param params.ticketId - チケットID（必須, 最大64文字）
 * @param params.targetStatus - 遷移先ステータス（SUBMITTED/IN_PROGRESS/REJECTED/CANCELLED）
 */
export const updateApprovalStatus = async (params: UpdateApprovalStatusParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_APPROVAL_STATUS, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * 承認API (API7122)
 * @param params.ticketId - チケットID（必須, 最大64文字。承認者本人のみ・自己承認禁止）
 */
export const approveTicket = async (params: ApproveTicketParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.APPROVE_TICKET, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};
